import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import "./consent-forms.css";

const PatientConsentForm = ({ onSubmit, isEditMode = false, existingData = null }) => {
  const [formData, setFormData] = useState({
    patientName: "",
    authorizeTest: false,
    acknowledgeWithdrawal: false,
    declineCounseling: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with existing data if in edit mode
  useEffect(() => {
    if (isEditMode && existingData) {
      setFormData({
        patientName: existingData.patient_name || "",
        authorizeTest: existingData.consent_checkboxes?.authorizeTest || false,
        acknowledgeWithdrawal: existingData.consent_checkboxes?.acknowledgeWithdrawal || false,
        declineCounseling: existingData.consent_checkboxes?.declineCounseling || false,
      });
    }
  }, [isEditMode, existingData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.patientName.trim()) {
      newErrors.patientName = "Patient name is required";
    }

    if (!formData.authorizeTest) {
      newErrors.authorizeTest =
        "You must authorize the laboratory to perform the test";
    }

    if (!formData.acknowledgeWithdrawal) {
      newErrors.acknowledgeWithdrawal =
        "You must acknowledge your right to withdraw consent";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);
      
      try {
        const now = new Date();
        const signatureDate = now.toISOString().split("T")[0];
        const signatureTime = now.toTimeString().split(" ")[0];

        const consentData = {
          consent_type: "patient",
          form_version: "v1.0",
          signature_date: signatureDate,
          signature_time: signatureTime,
          requisition_form_version: "v1.0",
          consent_checkboxes: {
            authorizeTest: formData.authorizeTest,
            acknowledgeWithdrawal: formData.acknowledgeWithdrawal,
            declineCounseling: formData.declineCounseling,
          },
          patient_name: formData.patientName,
          // Include existing consent form ID for updates
          ...(isEditMode && existingData?.id && { id: existingData.id }),
        };

        await onSubmit(consentData);
      } catch (error) {
        console.error("Error submitting consent form:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const isSubmitDisabled =
    !formData.authorizeTest ||
    !formData.acknowledgeWithdrawal ||
    !formData.patientName.trim() ||
    isSubmitting;

  return (
    <motion.div
      className="consent-form-container"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.3 }}
    >
      <div className="consent-form-header">
        <h1>Online Genetic Testing Consent Form</h1>
        <p>
          Please read each section carefully before signing. If you have any
          questions, contact our Genetic Counseling Department at{" "}
          <a
            href="https://mail.google.com/mail/?view=cm&fs=1&to=provider@syndicate.bio"
            target="_blank"
            rel="noopener noreferrer"
          >
            provider@syndicate.bio
          </a>.
        </p>
      </div>

      <div className="consent-form-content">
        <div className="consent-section">
          <h2>1. Self-Request</h2>
          <p>
            I, the undersigned patient, am voluntarily requesting the genetic
            testing services indicated on this requisition.
            <br />
            I acknowledge that I am seeking this test independently and not
            under the direct order of a treating physician.
          </p>
          <p>I confirm that I am requesting these services of my own accord.</p>
        </div>

        <div className="consent-section">
          <h2>2. Purpose of Testing</h2>
          <p>
            I understand that this test is intended to analyze my genetic
            information for potential insights related to cancer risk or
            treatment or other genetic testing as may be necessary.
            <br />
            I recognize that I am responsible for discussing any results with a
            qualified healthcare professional to determine appropriate follow-up
            or treatment.
          </p>
        </div>

        <div className="consent-section">
          <h2>3. Informed Decision</h2>
          <p>
            I confirm that I have been informed, to my satisfaction, of the
            following:
          </p>
          <ul>
            <li>The nature and purpose of the test</li>
            <li>The potential benefits</li>
            <li>The possible limitations and risks</li>
          </ul>
          <p>
            I have had the opportunity to ask questions about the testing
            process, and those questions have been answered to my satisfaction.
          </p>
        </div>

        <div className="consent-section">
          <h2>4. Data and Privacy</h2>
          <p>
            I understand how my sample and personal data will be collected, used,
            and disclosed, in accordance with applicable laws and privacy
            regulations. Specifically:
          </p>
          <ul>
            <li>
              The laboratory will collect, process, and store my sample and
              related data solely to perform the requested test.
            </li>
            <li>
              De-identified data may be used for quality assurance, laboratory
              validation, or research purposes, as permitted by law and the
              laboratory's policies.
            </li>
            <li>
              I understand that genetic information is inherently unique, and
              even with de-identification measures in place, there remains a
              potential risk of re-identification.
            </li>
            <li>
              I acknowledge that advances in technology and the ability to
              cross-reference multiple datasets may increase the chance that
              de-identified genetic data could be linked back to me.
            </li>
            <li>
              I recognize that there is no absolute guarantee of anonymity once
              my genetic data is shared in de-identified form.
            </li>
          </ul>
        </div>

        <div className="consent-section">
          <h2>5. Limitations & Follow-up</h2>
          <p>
            I acknowledge that genetic testing is complex and that results may
            have limitations. I understand:
          </p>
          <ul>
            <li>
              Test results are not a substitute for professional medical advice,
              diagnosis, or treatment.
            </li>
            <li>
              It is my responsibility to seek guidance from a qualified
              healthcare provider to interpret the results.
            </li>
            <li>
              Any medical or lifestyle decisions based on these results will be
              made in consultation with a healthcare professional.
            </li>
          </ul>
        </div>

        <div className="consent-section">
          <h2>6. Familial Implications</h2>
          <p>
            I understand that genetic information may have health or risk
            implications for my biological relatives.
          </p>
          <p>Due to privacy regulations, the company cannot directly share my
          genetic results with family members.</p>
          <p>I accept responsibility for informing relatives about any findings
          that may affect their health or medical decisions.</p>
        </div>

        <div className="consent-section">
          <h2>7. Right to Withdraw Consent/ Destroy Sample</h2>
          <p>
            I understand that I have the right to withdraw my consent for testing
            and to request destruction of my biological sample at any time prior
            to testing completion.
          </p>
          <p>I acknowledge the following limitations:</p>
          <ul>
            <li>
              Once testing is complete, physical samples may be retained for
              quality control, validation, or legal compliance.
            </li>
            <li>
              De-identified data already shared with researchers or included in
              aggregate analyses cannot be retrieved or destroyed.
            </li>
            <li>
              Samples or data held under court order or other legal requirements
              may not be eligible for destruction.
            </li>
          </ul>
        </div>

        <div className="consent-section">
          <h2>8. Genetic Counseling</h2>
          <p>
            Genetic testing can be complex and may raise questions about
            inheritance, disease risk, and medical management. Our service
            provides genetic counseling at no additional cost to help you:
          </p>
          <ul>
            <li>Understand the purpose and scope of testing</li>
            <li>Interpret test results and associated risks</li>
            <li>Explore implications for you and your family</li>
            <li>
              Discuss follow-up steps, including medical or lifestyle changes
            </li>
          </ul>
        </div>

        <div className="consent-section">
          <h2>9. Future Research Contact</h2>
          <p>
            I understand that I may be invited to participate in future research
            studies or clinical trials that could advance scientific knowledge
            and improve patient care.
          </p>
          <ul>
            <li>
              Participation in any future study or trial is voluntary and
              entirely separate from my current genetic testing.
            </li>
            <li>
              I will receive detailed information and separate consent forms
              before any future contact or enrollment.
            </li>
            <li>
              I may opt out of future contact at any time without affecting my
              current test results or clinical care.
            </li>
          </ul>
        </div>

        <div className="consent-section declaration">
          <h2>Participant Declaration</h2>
          <p>
            By ticking this box, I authorize the laboratory to perform the
            requested test(s) on my sample under these terms:
          </p>

          <div className="checkbox-group">
            <label className="custom-checkbox">
              <input
                type="checkbox"
                name="authorizeTest"
                checked={formData.authorizeTest}
                onChange={handleChange}
              />
              <span className="checkmark"></span>
              <span className="checkbox-text">
                I authorize the laboratory to perform the requested test(s) on
                my sample. (required)
              </span>
            </label>

            <label className="custom-checkbox">
              <input
                type="checkbox"
                name="acknowledgeWithdrawal"
                checked={formData.acknowledgeWithdrawal}
                onChange={handleChange}
              />
              <span className="checkmark"></span>
              <span className="checkbox-text">
                I acknowledge my right to withdraw consent and the associated
                limitations. (required)
              </span>
            </label>

            <label className="custom-checkbox">
              <input
                type="checkbox"
                name="declineCounseling"
                checked={formData.declineCounseling}
                onChange={handleChange}
              />
              <span className="checkmark"></span>
              <span className="checkbox-text">
                I decline genetic counseling and assume responsibility for
                interpreting my results. (optional)
              </span>
            </label>
          </div>

          {errors.authorizeTest && (
            <span className="error">{errors.authorizeTest}</span>
          )}
          {errors.acknowledgeWithdrawal && (
            <span className="error">{errors.acknowledgeWithdrawal}</span>
          )}
        </div>

        <div className="consent-section input-form-field">
          <h2>Patient information</h2>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              name="patientName"
              value={formData.patientName}
              onChange={handleChange}
              placeholder="Enter name here"
              className={errors.patientName ? "error" : ""}
            />
            {errors.patientName && (
              <span className="error">{errors.patientName}</span>
            )}
          </div>
        </div>
      </div>

      <div className="consent-form-footer">
        <button
          type="submit"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          className={`submit-button ${isSubmitDisabled ? "disabled" : ""}`}
        >
          {isSubmitting 
            ? (isEditMode ? "Updating..." : "Submitting...") 
            : (isEditMode ? "Update form" : "Submit form")
          }
        </button>
      </div>
    </motion.div>
  );
};

export default PatientConsentForm;
